import 'date-fns';

import DateFnsUtils from '@date-io/date-fns';
import {
	Checkbox,
	CircularProgress,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControlLabel,
	Table,
	TableHead,
	TableBody,
	TableCell,
	TableRow,
	TextField,
	Button,
	Typography,
	Menu,
	MenuItem,
	IconButton,
	WithStyles,
	withStyles,
} from '@material-ui/core';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import {
	MuiPickersUtilsProvider,
	KeyboardDatePicker,
} from '@material-ui/pickers';
import {MaterialUiPickersDate} from '@material-ui/pickers/typings/date';
import PopupState, {bindMenu, bindTrigger} from 'material-ui-popup-state';
import moment from 'moment';
import {ChangeEvent, Component, Fragment, ReactNode} from 'react';

import AuthService from './../../Common/AuthService';
import {
	UserDataModel,
	WorkingWeekdays,
	SpecialVacation,
} from './../UserData.Model';
import editUserStyles from './EditUserStyles';
import WorkdaysModal from './WorkdaysModal';
import SpecialVacationModal from '../SpecialVacation/SpecialVacationModal';

interface State {
	editUser: UserDataModel;
	selectedMonth: number;
	selectedYear: number;
	dialogOpen: boolean;
	workdaysDialogOpen: boolean;
	specialVacationDialogOpen: boolean;
	selectError: boolean;
	menuAnchor: Element | null;
	workingWeekdaysToEdit: WorkingWeekdays | null;
	idx: number;
	specialVacationToEdit: SpecialVacation | null;
	isLoading: boolean;
}

interface Props extends WithStyles<typeof editUserStyles> {
	selectedUser: UserDataModel | null;
	closeHandler: () => void;
}

class EditUserModal extends Component<Props, State> {
	private readonly authService: AuthService;

	public constructor(props: Props) {
		super(props);
		this.authService = new AuthService();

		let user = this.props.selectedUser;
		user ??= {
			_id: undefined,
			firstName: '',
			lastName: '',
			password: '',
			email: '',
			loxoneId: '',
			rocketChat: '',
			isAdmin: false,
			isArchived: false,
			workingWeekdays: [],
			specialVacations: [],
			entryDate: new Date(),
		};

		this.state = {
			editUser: user,
			dialogOpen: true,
			workdaysDialogOpen: false,
			selectedMonth: moment().month() + 1,
			selectedYear: moment().year(),
			selectError: false,
			menuAnchor: null,
			workingWeekdaysToEdit: null,
			idx: -1,
			specialVacationDialogOpen: false,
			specialVacationToEdit: null,
			isLoading: false,
		};
	}

	private changeEntryDate(date: MaterialUiPickersDate): void {
		if (date) {
			const editUser = this.state.editUser;
			editUser.entryDate = new Date(date.toDateString());
			this.setState({editUser});
		}
	}

	public alterUserDetails(
		prop: keyof Omit<
			UserDataModel,
			'specialVacations' | 'workingWeekdays' | 'entryDate'
		>,
		e: ChangeEvent<
			HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
		>
	): void {
		const curState: UserDataModel = this.state.editUser;

		if (prop === 'isAdmin' || prop === 'isArchived') {
			curState[prop] = !curState[prop];
		} else {
			curState[prop] = e.target.value;
		}

		this.setState({editUser: curState});
	}

	public buildInputElementsByKey(): ReactNode {
		const classes = this.props.classes;
		const options = ['Bearbeiten', 'Löschen'];

		return (
			<div>
				<div>
					<div className={this.props.classes.inputGroup}>
						<div className={this.props.classes.dialogInput}>
							<TextField
								value={this.state.editUser.email}
								onChange={this.alterUserDetails.bind(
									this,
									'email'
								)}
								label='E-Mail Adresse'
								fullWidth
							/>
						</div>
						<div className={this.props.classes.dialogInput}>
							<TextField
								value={this.state.editUser.password}
								onChange={this.alterUserDetails.bind(
									this,
									'password'
								)}
								type='password'
								label='Passwort'
								fullWidth
							/>
						</div>
					</div>
					<div className={this.props.classes.inputGroup}>
						<div className={this.props.classes.dialogInput}>
							<TextField
								value={this.state.editUser.firstName}
								onChange={this.alterUserDetails.bind(
									this,
									'firstName'
								)}
								label='Vorname'
								fullWidth
							/>
						</div>
						<div className={this.props.classes.dialogInput}>
							<TextField
								value={this.state.editUser.lastName}
								onChange={this.alterUserDetails.bind(
									this,
									'lastName'
								)}
								label='Nachname'
								fullWidth
							/>
						</div>
					</div>
					<div className={this.props.classes.inputGroup}>
						<div className={this.props.classes.dialogInput}>
							<TextField
								value={this.state.editUser.rocketChat}
								onChange={this.alterUserDetails.bind(
									this,
									'rocketChat'
								)}
								label='Rocket.Chat'
								fullWidth
							/>
						</div>
						<div className={this.props.classes.dialogInput}>
							<TextField
								value={this.state.editUser.openProject}
								onChange={this.alterUserDetails.bind(
									this,
									'openProject'
								)}
								label='OpenProject'
								fullWidth
							/>
						</div>
						<div className={this.props.classes.dialogInput}>
							<TextField
								value={this.state.editUser.loxoneId}
								onChange={this.alterUserDetails.bind(
									this,
									'loxoneId'
								)}
								label='Loxone'
								fullWidth
							/>
						</div>
					</div>
					<div className={this.props.classes.inputGroup}>
						<div className={this.props.classes.dialogInput}>
							<MuiPickersUtilsProvider utils={DateFnsUtils}>
								<KeyboardDatePicker
									disableToolbar
									variant='inline'
									format='dd. MM. yyyy'
									id='date-picker-entryDate'
									label='Eintrittstag'
									value={
										this.state.editUser.entryDate
											? this.state.editUser.entryDate
											: null
									}
									onChange={(date): void => {
										this.changeEntryDate(date);
									}}
									KeyboardButtonProps={{
										'aria-label': 'change entry date',
									}}
									className={classes.entryDate}
									autoOk
								/>
							</MuiPickersUtilsProvider>
						</div>
					</div>
					<div>
						<FormControlLabel
							control={
								<Checkbox
									onChange={this.alterUserDetails.bind(
										this,
										'isAdmin'
									)}
									checked={this.state.editUser.isAdmin}
								/>
							}
							label='Administrator'
						/>
					</div>
					<div>
						<FormControlLabel
							control={
								<Checkbox
									onChange={this.alterUserDetails.bind(
										this,
										'isArchived'
									)}
									checked={this.state.editUser.isArchived}
								/>
							}
							label='Archiviert'
						/>
					</div>
				</div>
				<div className={this.props.classes.listBlock}>
					<Typography variant='h4'>Standardarbeitszeiten</Typography>
					<Button
						onClick={(): void => {
							this.setState({
								workdaysDialogOpen: true,
								workingWeekdaysToEdit: null,
							});
						}}
						variant='contained'
						color='secondary'
						style={{marginTop: '16px'}}
						disabled={this.state.isLoading}
					>
						Neue Arbeitszeit
					</Button>
					<Table style={{margin: '15px'}}>
						<TableHead>
							<TableRow>
								<TableCell>Zeitraum</TableCell>
								<TableCell>Arbeitszeiten</TableCell>
								<TableCell style={{width: '10%'}}>
									Optionen
								</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{this.state.editUser?.workingWeekdays?.map(
								(
									workingWeekdays: WorkingWeekdays,
									index: number
								) => (
									<TableRow key={index}>
										<TableCell
											key={index.toString() + 'date'}
										>
											{moment(
												workingWeekdays.startDate
											).format('DD. MM. YYYY')}{' '}
											-{' '}
											{moment(
												workingWeekdays.endDate
											).format('DD. MM. YYYY')}
										</TableCell>
										<TableCell
											key={index.toString() + 'weekdays'}
										>
											{'Montag: ' +
												workingWeekdays.weekdays[0].toString() +
												'h | Dienstag: ' +
												workingWeekdays.weekdays[1].toString() +
												'h | Mittwoch: ' +
												workingWeekdays.weekdays[2].toString() +
												'h | Donnerstag: ' +
												workingWeekdays.weekdays[3].toString() +
												'h | Freitag: ' +
												workingWeekdays.weekdays[4].toString() +
												'h'}
										</TableCell>
										<TableCell>
											<PopupState
												key={
													'popupStateobj' +
													index.toString()
												}
												variant='popover'
												popupId='edit-working-times-menu'
											>
												{(
													popupState: Window
												): ReactNode => (
													<Fragment
														key={
															'popupState' +
															index.toString()
														}
													>
														<IconButton
															{...bindTrigger(
																popupState
															)}
														>
															<MoreVertIcon />
														</IconButton>
														<Menu
															{...bindMenu(
																popupState
															)}
														>
															{options.map(
																(
																	option
																): ReactNode => (
																	<MenuItem
																		key={
																			option
																		}
																		onClick={() => {
																			if (
																				option ===
																				'Bearbeiten'
																			) {
																				this.setState(
																					{
																						workdaysDialogOpen:
																							true,
																						workingWeekdaysToEdit:
																							JSON.parse(
																								JSON.stringify(
																									workingWeekdays
																								)
																							),
																						idx: index,
																					}
																				);
																			} else if (
																				option ===
																				'Löschen'
																			) {
																				this.deleteWorkingWeekdays(
																					index
																				);
																			}
																			popupState.close();
																		}}
																	>
																		{option}
																	</MenuItem>
																)
															)}
														</Menu>
													</Fragment>
												)}
											</PopupState>
										</TableCell>
									</TableRow>
								)
							)}
						</TableBody>
					</Table>
					<WorkdaysModal
						workingWeekdays={this.state.workingWeekdaysToEdit}
						closeHandler={this.closeModal.bind(this)}
						dialogOpen={this.state.workdaysDialogOpen}
						handleWorkingWeekdays={this.handleWorkingWeekdays.bind(
							this
						)}
						index={this.state.idx}
					/>
				</div>
				<div className={this.props.classes.listBlock}>
					<Typography variant='h4'>Sonderurlaub</Typography>
					<Button
						onClick={(): void => {
							this.setState({
								specialVacationDialogOpen: true,
								specialVacationToEdit: null,
							});
						}}
						variant='contained'
						color='secondary'
						style={{marginTop: '16px'}}
						disabled={this.state.isLoading}
					>
						Neuer Sonderurlaub
					</Button>
					<Table style={{margin: '15px'}}>
						<TableHead>
							<TableRow>
								<TableCell>Zeitraum</TableCell>
								<TableCell>Anzahl Sonderurlaubstage</TableCell>
								<TableCell style={{width: '10%'}}>
									Optionen
								</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{this.state.editUser?.specialVacations.map(
								(
									specialVacation: SpecialVacation,
									index: number
								) => (
									<TableRow key={index}>
										<TableCell
											key={index.toString() + 'date'}
										>
											{EditUserModal.getMonthName(
												new Date(
													specialVacation.date
												).getMonth() + 1
											)}{' '}
											{new Date(
												specialVacation.date
											).getFullYear()}
										</TableCell>
										<TableCell
											key={index.toString() + 'days'}
										>
											{specialVacation.days}
										</TableCell>
										<TableCell>
											<PopupState
												key={
													'popupStateobj' +
													index.toString()
												}
												variant='popover'
												popupId='edit-special-vacation-menu'
											>
												{(
													popupState: Window
												): ReactNode => (
													<Fragment
														key={
															'popupState' +
															index.toString()
														}
													>
														<IconButton
															{...bindTrigger(
																popupState
															)}
														>
															<MoreVertIcon />
														</IconButton>
														<Menu
															{...bindMenu(
																popupState
															)}
														>
															{options.map(
																(
																	option
																): ReactNode => (
																	<MenuItem
																		key={
																			option
																		}
																		onClick={() => {
																			if (
																				option ===
																				'Bearbeiten'
																			) {
																				this.setState(
																					{
																						specialVacationDialogOpen:
																							true,
																						specialVacationToEdit:
																							JSON.parse(
																								JSON.stringify(
																									specialVacation
																								)
																							),
																					}
																				);
																			} else if (
																				option ===
																				'Löschen'
																			) {
																				this.deleteSpecialVacation(
																					specialVacation
																				);
																			}
																			popupState.close();
																		}}
																	>
																		{option}
																	</MenuItem>
																)
															)}
														</Menu>
													</Fragment>
												)}
											</PopupState>
										</TableCell>
									</TableRow>
								)
							)}
						</TableBody>
					</Table>
					<SpecialVacationModal
						specialVacation={this.state.specialVacationToEdit}
						closeHandler={this.closeModal.bind(this)}
						dialogOpen={this.state.specialVacationDialogOpen}
						handleSpecialVacation={this.handleSpecialVacation.bind(
							this
						)}
						selectedUser={this.state.editUser}
						specialVacationExists={this.specialVacationExists.bind(
							this
						)}
					/>
				</div>
			</div>
		);
	}

	public closeModal(): void {
		this.setState({
			workdaysDialogOpen: false,
			specialVacationDialogOpen: false,
		});
	}

	public getNext3Years(): number[] {
		const curYear = new Date().getFullYear();
		const years = [];

		for (let i = 0; i < 3; i++) {
			years.push(curYear + i);
		}

		return years;
	}

	public selectChanged(
		event: React.ChangeEvent<{name?: string; value: unknown}>,
		_: ReactNode,
		selectionType: string
	): void {
		if (selectionType === 'month') {
			this.setState({
				selectedMonth: parseInt(event.target.value as string, 10),
			});
		} else if (selectionType === 'year') {
			this.setState({
				selectedYear: parseInt(event.target.value as string, 10),
			});
		}
	}

	private handleWorkingWeekdays(
		newWorkingWeekdays: WorkingWeekdays,
		isNew: boolean,
		index: number
	): void {
		if (this.state.editUser.workingWeekdays) {
			if (isNew) {
				this.state.editUser.workingWeekdays.push(newWorkingWeekdays);
			} else {
				const currentUser = this.state.editUser;
				if (currentUser.workingWeekdays) {
					currentUser.workingWeekdays[index] = newWorkingWeekdays;
					this.setState({editUser: currentUser});
				}
			}
		}
	}

	private handleSpecialVacation(newSpecialVacation: SpecialVacation): void {
		const currentUser = this.state.editUser;
		const specialVacations: SpecialVacation[] = [];
		const newSpecialVacationDate = new Date(newSpecialVacation.date);

		for (const specialVacation of currentUser.specialVacations) {
			const specialVacationDate = new Date(specialVacation.date);
			if (
				specialVacationDate.getMonth() ===
					newSpecialVacationDate.getMonth() &&
				specialVacationDate.getFullYear() ===
					newSpecialVacationDate.getFullYear()
			) {
				specialVacations.push(newSpecialVacation);
			} else {
				specialVacations.push(specialVacation);
			}
		}
		if (!this.specialVacationExists(newSpecialVacation)) {
			specialVacations.push(newSpecialVacation);
		}

		currentUser.specialVacations = specialVacations;
		this.setState({editUser: currentUser});
	}

	private deleteWorkingWeekdays(index: number): void {
		const currentUser = this.state.editUser;
		if (currentUser.workingWeekdays) {
			currentUser.workingWeekdays.splice(index, 1);
		}
		this.setState({editUser: currentUser});
	}

	private deleteSpecialVacation(
		specialVacationToDelete: SpecialVacation
	): void {
		const currentUser = this.state.editUser;
		const specialVacations = [];
		const specialVacationToDeleteDate = new Date(
			specialVacationToDelete.date
		);

		for (const specialVacation of currentUser.specialVacations) {
			const specialVacationDate = new Date(specialVacation.date);
			if (
				!(
					specialVacationDate.getMonth() ===
						specialVacationToDeleteDate.getMonth() &&
					specialVacationDate.getFullYear() ===
						specialVacationToDeleteDate.getFullYear()
				)
			) {
				specialVacations.push(specialVacation);
			}
		}
		currentUser.specialVacations = specialVacations;
		this.setState({editUser: currentUser});
	}

	private closeEditor(): void {
		this.props.closeHandler();
	}

	private saveUser(): void {
		this.setState({isLoading: true});
		this.authService
			.createOrChangeUser(this.state.editUser)
			.then((): void => {
				this.props.closeHandler();
				this.setState({isLoading: false});
			});
	}

	public render(): ReactNode {
		const classes = this.props.classes;

		return (
			<div>
				<DialogTitle>
					{`Benutzer ${this.state.editUser.firstName ?? ''} ${this.state.editUser.lastName ?? ''} bearbeiten`}
				</DialogTitle>
				<DialogContent className={classes.dialogBox}>
					<Typography>Allgemein</Typography>
					{this.buildInputElementsByKey()}
					<div className={classes.progressContainer}>
						{this.state.isLoading && (
							<CircularProgress className={classes.progress} />
						)}
					</div>
				</DialogContent>

				<DialogActions style={{marginBottom: '16px'}}>
					<Button
						onClick={this.closeEditor.bind(this)}
						variant='outlined'
						color='secondary'
						disabled={this.state.isLoading}
					>
						Abbrechen
					</Button>
					<Button
						onClick={this.saveUser.bind(this)}
						variant='contained'
						color='secondary'
						disabled={this.state.isLoading}
					>
						Speichern
					</Button>
				</DialogActions>
			</div>
		);
	}

	private static getMonthName(selectedMonth: number): string {
		const months: string[] = [
			'Kein',
			'Jänner',
			'Februar',
			'März',
			'April',
			'Mai',
			'Juni',
			'Juli',
			'August',
			'September',
			'Oktober',
			'November',
			'Dezember',
		];

		return months[selectedMonth];
	}

	private specialVacationExists(
		newSpecialVacation: SpecialVacation
	): boolean {
		for (const specialVacation of this.state.editUser.specialVacations) {
			const specialVacationDate = new Date(specialVacation.date);
			const newSpecialVacationDate = new Date(newSpecialVacation.date);
			if (
				specialVacationDate.getMonth() ===
					newSpecialVacationDate.getMonth() &&
				specialVacationDate.getFullYear() ===
					newSpecialVacationDate.getFullYear()
			) {
				return true;
			}
		}
		return false;
	}
}

export default withStyles(editUserStyles)(EditUserModal);
